import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWarehouse,
  faTruck,
  faSpinner,
  faExclamationTriangle,
  faClock,
  faMapMarkerAlt,
  faUser,
  faBarcode,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faShippingFast,
  faClipboardList,
  faCalendarAlt,
  faBuilding,
  faIdCard,
  faArrowRight,
  faArrowLeft,
  faHistory,
  faTag,
  faBox,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

const YardTab = ({ item }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDelivery, setExpandedDelivery] = useState(null);

  useEffect(() => {
    const fetchDeliveries = async () => {
      try {
        if (!item?.header?.trailer_number) {
          setLoading(false);
          setError("No trailer number available");
          return;
        }

        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/deliveries`,
          {
            params: {
              trailerNumber: item.header.trailer_number,
            },
          }
        );

        setDeliveries(Array.isArray(response.data) ? response.data : []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching deliveries:", error);
        if (error.response?.status === 404) {
          setError("No yard movements found for this trailer");
        } else {
          setError("Failed to load yard data. Please try again later.");
        }
        setLoading(false);
      }
    };

    if (item?.header?.trailer_number) {
      fetchDeliveries();
    } else {
      setLoading(false);
    }
  }, [item]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "out_of_yard":
        return { icon: faArrowRight, color: "text-green-600" };
      case "dispatched":
        return { icon: faShippingFast, color: "text-blue-600" };
      case "cancelled":
        return { icon: faTimesCircle, color: "text-red-600" };
      case "not_dispatched_cancelled":
        return { icon: faTimesCircle, color: "text-red-600" };
      case "assigned":
        return { icon: faClipboardList, color: "text-yellow-600" };
      default:
        return { icon: faHourglassHalf, color: "text-gray-600" };
    }
  };

  const getGateStatusBadge = (status) => {
    const statusMap = {
      registering: {
        label: "Registering",
        class: "bg-yellow-100 text-yellow-800",
      },
      ctpat_validation: {
        label: "CTPAT Validation",
        class: "bg-blue-100 text-blue-800",
      },
      photo_validation: {
        label: "Photo Validation",
        class: "bg-purple-100 text-purple-800",
      },
      registered: { label: "Registered", class: "bg-green-100 text-green-800" },
      rejected: { label: "Rejected", class: "bg-red-100 text-red-800" },
      registered_out: {
        label: "Registered Out",
        class: "bg-gray-100 text-gray-800",
      },
    };

    const statusInfo = statusMap[status] || {
      label: status || "N/A",
      class: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.class}`}
      >
        {statusInfo.label}
      </span>
    );
  };

  const toggleDeliveryExpansion = (deliveryId) => {
    setExpandedDelivery(expandedDelivery === deliveryId ? null : deliveryId);
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 1) {
      const diffMinutes = Math.round(diffMs / (1000 * 60));
      return `${diffMinutes} min`;
    }

    return `${diffHours.toFixed(1)} hrs`;
  };

  // Function to get icon for status history
  const getStatusHistoryIcon = (status, statusType) => {
    const statusIconMap = {
      // Status type icons
      in_yard: { icon: faWarehouse, color: "text-blue-500" },
      not_dispatched_cancelled: { icon: faTimesCircle, color: "text-red-500" },
      dispatched: { icon: faShippingFast, color: "text-green-500" },
      out_of_yard: { icon: faArrowRight, color: "text-purple-500" },
      assigned: { icon: faClipboardList, color: "text-yellow-500" },
      cancelled: { icon: faTimesCircle, color: "text-red-600" },

      // Gate status icons
      registering: { icon: faClipboardList, color: "text-yellow-500" },
      ctpat_validation: { icon: faCheckCircle, color: "text-blue-500" },
      photo_validation: { icon: faCheckCircle, color: "text-purple-500" },
      registered: { icon: faCheckCircle, color: "text-green-500" },
      rejected: { icon: faTimesCircle, color: "text-red-500" },
      registered_out: { icon: faArrowRight, color: "text-gray-500" },

      // Kiosk status icons
      booking: { icon: faCalendarAlt, color: "text-blue-500" },
      expired: { icon: faExclamationTriangle, color: "text-orange-500" },
      trailer_dropped: { icon: faTruck, color: "text-indigo-500" },
      waiting_pickup: { icon: faHourglassHalf, color: "text-yellow-500" },

      // Dock status icons
      loading: { icon: faBox, color: "text-blue-500" },
      unloading: { icon: faBox, color: "text-green-500" },

      // Default
      default: { icon: faHistory, color: "text-gray-500" },
    };

    return statusIconMap[status] || statusIconMap["default"];
  };

  // Function to format status type for display
  const formatStatusType = (statusType) => {
    const typeMap = {
      status: "System Status",
      gateStatus: "Gate Status",
      kioskStatus: "Kiosk Status",
      tractorStatus: "Tractor Status",
      dockStatus: "Dock Status",
    };

    return typeMap[statusType] || statusType;
  };

  // Function to format status name for display
  const formatStatusName = (status) => {
    if (!status) return "Unknown";

    // Special formatting for specific statuses
    const statusNameMap = {
      not_dispatched_cancelled: "Not Dispatched - Cancelled",
      ctpat_validation: "CTPAT Validation",
      photo_validation: "Photo Validation",
      registered_out: "Registered Out",
      trailer_dropped: "Trailer Dropped",
      waiting_pickup: "Waiting for Pickup",
      in_yard: "In Yard",
      out_of_yard: "Out of Yard",
    };

    return (
      statusNameMap[status] ||
      status.replace(/_/g, " ").charAt(0).toUpperCase() +
        status.replace(/_/g, " ").slice(1)
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-blue-500 text-4xl animate-spin mb-4"
        />
        <p className="text-gray-600">Loading yard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col items-center justify-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-yellow-500 text-4xl mb-4"
          />
          <p className="text-gray-700 mb-2 font-medium">{error}</p>
          <p className="text-gray-500 text-sm">
            Trailer Number: {item?.header?.trailer_number || "Unknown"}
          </p>
        </div>
      </div>
    );
  }

  if (deliveries.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col items-center justify-center">
          <FontAwesomeIcon
            icon={faWarehouse}
            className="text-gray-400 text-4xl mb-4"
          />
          <p className="text-gray-700 mb-2 font-medium">
            No yard movements found
          </p>
          <p className="text-gray-500 text-sm">
            There are no yard records for trailer {item?.header?.trailer_number}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
          <FontAwesomeIcon icon={faWarehouse} className="mr-2 text-blue-600" />
          Yard Movement Summary
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-xs text-gray-500">Total Movements:</span>
            <p className="font-medium">{deliveries.length}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Trailer Number:</span>
            <p className="font-medium">{item.header.trailer_number}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Latest Status:</span>
            <p className="font-medium flex items-center">
              <FontAwesomeIcon
                icon={getStatusIcon(deliveries[0]?.status).icon}
                className={`mr-1 ${getStatusIcon(deliveries[0]?.status).color}`}
              />
              {deliveries[0]?.status?.replace(/_/g, " ").toUpperCase() || "N/A"}
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Warehouse:</span>
            <p className="font-medium">{deliveries[0]?.warehouse || "N/A"}</p>
          </div>
        </div>
      </div>

      {/* Movement Overview Card */}
      {deliveries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
            <FontAwesomeIcon
              icon={faClipboardList}
              className="mr-2 text-blue-500"
            />
            Movement Overview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {deliveries.slice(0, 3).map((delivery, index) => (
              <div key={delivery._id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">
                    Movement #{index + 1}
                  </span>
                  {getGateStatusBadge(delivery.gateStatus)}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center text-sm">
                    <FontAwesomeIcon
                      icon={faBarcode}
                      className="text-gray-400 mr-2 text-xs"
                    />
                    <span className="font-medium">
                      {delivery.reservationCode}
                    </span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <FontAwesomeIcon
                      icon={faClock}
                      className="text-gray-400 mr-2"
                    />
                    <span>{formatDate(delivery.createdAt)}</span>
                  </div>
                  {delivery.dockNumber && (
                    <div className="flex items-center text-xs text-gray-600">
                      <FontAwesomeIcon
                        icon={faWarehouse}
                        className="text-gray-400 mr-2"
                      />
                      <span>{delivery.dockNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {deliveries.length > 3 && (
              <div className="p-3 bg-blue-50 rounded-lg flex items-center justify-center">
                <p className="text-sm text-blue-700 text-center">
                  +{deliveries.length - 3} more movements
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deliveries List */}
      {deliveries.map((delivery, index) => (
        <div
          key={delivery._id}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          {/* Delivery Header - Clickable */}
          <div
            className={`flex justify-between items-center px-4 py-3 cursor-pointer ${
              expandedDelivery === delivery._id
                ? "bg-blue-50"
                : "hover:bg-gray-50"
            }`}
            onClick={() => toggleDeliveryExpansion(delivery._id)}
          >
            <div className="flex items-center">
              <FontAwesomeIcon
                icon={faWarehouse}
                className="text-blue-600 mr-3"
              />
              <div>
                <h3 className="font-medium text-gray-900">
                  Movement #{index + 1} - {delivery.reservationCode}
                </h3>
                <p className="text-xs text-gray-500">
                  {formatDate(delivery.createdAt)} | {delivery.warehouse}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getGateStatusBadge(delivery.gateStatus)}
              <FontAwesomeIcon
                icon={
                  expandedDelivery === delivery._id ? faArrowLeft : faArrowRight
                }
                className="text-gray-500"
              />
            </div>
          </div>

          {/* Expanded Delivery Details */}
          {expandedDelivery === delivery._id && (
            <div className="border-t border-gray-200 p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Movement Information */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                    Movement Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Status:</span>
                      <span className="text-sm font-medium">
                        {delivery.status?.replace(/_/g, " ").toUpperCase() ||
                          "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Type:</span>
                      <span className="text-sm">
                        {delivery.pickupDelivery === "pickup"
                          ? "Pickup"
                          : "Delivery"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">
                        Delivery Type:
                      </span>
                      <span className="text-sm capitalize">
                        {delivery.deliveryType || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Office:</span>
                      <span className="text-sm">
                        {delivery.office || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Equipment Information */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                    Equipment Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Tractor:</span>
                      <span className="text-sm">
                        {delivery.tractorNumber || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Carrier:</span>
                      <span className="text-sm">
                        {delivery.carrierName || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Driver:</span>
                      <span className="text-sm">
                        {delivery.driverName || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Owner:</span>
                      <span className="text-sm">
                        {delivery.equipmentOwner || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Dock Information */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                    Dock Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Dock:</span>
                      <span className="text-sm">
                        {delivery.dockNumber || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">
                        Dock Status:
                      </span>
                      <span className="text-sm">
                        {delivery.dockStatus || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">
                        Kiosk Status:
                      </span>
                      <span className="text-sm">
                        {delivery.kioskStatus
                          ?.replace(/_/g, " ")
                          .toUpperCase() || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Duration:</span>
                      <span className="text-sm">
                        {delivery.elapsedWhseTime
                          ? `${delivery.elapsedWhseTime.toFixed(1)} hrs`
                          : calculateDuration(
                              delivery.createdAt,
                              delivery.closedAt
                            )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* Dates */}
                <div className="bg-gray-50 p-3 rounded">
                  <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                    Important Dates
                  </h4>
                  <div className="space-y-2">
                    {delivery.createdAt && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Created:</span>
                        <span className="text-sm">
                          {formatDate(delivery.createdAt)}
                        </span>
                      </div>
                    )}
                    {delivery.assignedDate && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Assigned:</span>
                        <span className="text-sm">
                          {formatDate(delivery.assignedDate)}
                        </span>
                      </div>
                    )}
                    {delivery.floorJobCreated && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">
                          Floor Job:
                        </span>
                        <span className="text-sm">
                          {formatDate(delivery.floorJobCreated)}
                        </span>
                      </div>
                    )}
                    {delivery.closedAt && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Closed:</span>
                        <span className="text-sm">
                          {formatDate(delivery.closedAt)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-gray-50 p-3 rounded">
                  <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                    Additional Information
                  </h4>
                  <div className="space-y-2">
                    {delivery.customer && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Customer:</span>
                        <span className="text-sm">{delivery.customer}</span>
                      </div>
                    )}
                    {delivery.sealNumber && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Seal #:</span>
                        <span className="text-sm">{delivery.sealNumber}</span>
                      </div>
                    )}
                    {delivery.equipmentPlates && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Plates:</span>
                        <span className="text-sm">
                          {delivery.equipmentPlates}{" "}
                          {delivery.equipmentPlateState &&
                            `(${delivery.equipmentPlateState})`}
                        </span>
                      </div>
                    )}
                    {delivery.trailerStatus && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">
                          Trailer Status:
                        </span>
                        <span className="text-sm">
                          {delivery.trailerStatus}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Status History Timeline */}
              {delivery.statusHistory && delivery.statusHistory.length > 0 && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
                    <FontAwesomeIcon
                      icon={faHistory}
                      className="mr-2 text-blue-500"
                    />
                    Movement Timeline
                  </h4>
                  <div className="relative pl-8">
                    {/* Timeline line */}
                    <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-blue-200"></div>

                    {/* Timeline events */}
                    {delivery.statusHistory.map((history, idx) => {
                      const statusIcon = getStatusHistoryIcon(
                        history.status,
                        history.statusType
                      );
                      return (
                        <div key={idx} className="mb-6 relative">
                          {/* Timeline dot */}
                          <div
                            className={`absolute -left-4 mt-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ${statusIcon.color.replace(
                              "text-",
                              "bg-"
                            )}`}
                          ></div>

                          <div className="text-sm">
                            <div className="text-xs text-gray-500 mb-1 flex items-center justify-between">
                              <span>{formatDate(history.createdAt)}</span>
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs ${
                                  history.statusType === "status"
                                    ? "bg-blue-100 text-blue-800"
                                    : history.statusType === "gateStatus"
                                    ? "bg-purple-100 text-purple-800"
                                    : history.statusType === "kioskStatus"
                                    ? "bg-green-100 text-green-800"
                                    : history.statusType === "tractorStatus"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {formatStatusType(history.statusType)}
                              </span>
                            </div>
                            <div className="font-medium text-gray-900 flex items-center">
                              <FontAwesomeIcon
                                icon={statusIcon.icon}
                                className={`mr-2 ${statusIcon.color}`}
                              />
                              {formatStatusName(history.status)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default YardTab;
